import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import CheckCircle from "@repo/icons/default/CheckCircle";
import Clock from "@repo/icons/default/Clock";
import Left from "@repo/icons/default/Left";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import {
  EmptyStateButton,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateRoot,
  EmptyStateTitle,
} from "@/components/ui/empty-state/empty-state";

import type { JSX } from "react";

interface RecipientStatusSearch {
  status: "success" | "pending" | "error";
}

const RecipientStatusPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { status } = useSearch({ from: "/settings/payments/create-recipient/status" });

  const isSuccess = status === "success";
  const isPending = status === "pending";

  const getIconFill = () => {
    if (isSuccess) return "secondary";
    if (isPending) return "secondary";
    return "neutral";
  };

  const getIcon = () => {
    if (isSuccess) return CheckCircle;
    return Clock;
  };

  const getTitle = () => {
    if (isSuccess) return "Sua conta foi cadastrada com sucesso";
    if (isPending) return "Seus dados foram enviados para análise";
    return "Erro ao criar conta";
  };

  const getDescription = () => {
    if (isSuccess) {
      return "Agora você já pode usufruir de todos os benefícios da nossa solução de pagamentos.";
    }
    if (isPending) {
      return "Aguarde a aprovação da sua conta e em breve você poderá usufruir de todos os benefícios da nossa solução de pagamentos.";
    }
    return "Ocorreu um problema ao criar sua conta recebedora. Por favor, tente novamente mais tarde.";
  };

  const getButtonLabel = () => {
    if (isSuccess) return "Começar";
    if (isPending) return "Voltar para Menu";
    return "Voltar";
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton
          align="start"
          icon={Left}
          onClick={() => {
            const destination = isPending ? "/settings" : "/settings/payments";
            void navigate({ to: destination, search: { from: "create-recipient" } });
          }}
        />
        <HeaderTitle title="Cadastrar conta" align="center" />
      </HeaderRoot>
      <Page className="px-6 py-8">
        <EmptyStateRoot>
          <EmptyStateIcon fill={getIconFill()} icon={getIcon()} />

          <EmptyStateTitle>{getTitle()}</EmptyStateTitle>

          <EmptyStateDescription>{getDescription()}</EmptyStateDescription>

          <EmptyStateButton
            fullWidth
            onClick={() => {
              const destination =
                isPending || isSuccess ? "/settings" : "/settings/payments";
              void navigate({
                to: destination,
                search: { from: "create-recipient" },
              });
            }}
          >
            {getButtonLabel()}
          </EmptyStateButton>
        </EmptyStateRoot>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/payments/create-recipient/status")({
  component: RecipientStatusPage,
  validateSearch: (search: Record<string, unknown>): RecipientStatusSearch => {
    return {
      status: search.status as "success" | "pending" | "error",
    };
  },
});
